import ReportRenderer
    from '@src/components/features/reports-and-logs/new-reports/report-viewer/report-renderer/report-renderer';
import { TryAgainMessage } from '@src/components/shared/try-again-message/try-again-message';
import Loader from '@src/components/shared/loader/loader';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ReportName } from '@src/utils/enums';
import {
    ReportFilterState
} from '@src/components/features/reports-and-logs/new-reports/report-viewer/useReportFilters';
import { useStores } from '@src/components/store';
import { observer } from 'mobx-react-lite';
import {
    AllReportsFieldsConfig
} from '@src/components/features/reports-and-logs/report-widget-factory/all-top10-columns-config';
import { reportFilters } from '@src/dtos/reports.dto';

interface ReportContentProps {
    reportName: ReportName;
    isLoading: boolean;
    setIsLoading: (value: boolean) => void;
    filters?: ReportFilterState;
    filtersReportName?: ReportName;
    searchValue?: string;
}

export const ReportContent: FC<ReportContentProps> = observer(({reportName, isLoading, setIsLoading, filters, filtersReportName, searchValue}) => {
    const [data, setData] = useState<AllReportsFieldsConfig[] | undefined>(undefined);
    const [hasError, setHasError] = useState(false);
    const {reportsStore, userPreferencesStore} = useStores();

    const filteredData = useMemo(() => {
        if (!data) return undefined;
        if (!searchValue) return data;

        return data.filter(item =>
            Object.values(item).some(v =>
                String(v).toLowerCase().includes(searchValue.toLowerCase())));
    }, [data, searchValue]);

    const loadReportData = useCallback(async () => {
        setIsLoading(true);
        setHasError(false);
        try {
            const result = await reportsStore.loadNewReportData(reportName, filters);
            setData(result || []);
        } catch (error: any) {
            if (error.name !== 'CanceledError') setHasError(true);
        } finally {
            setIsLoading(false);
        }
    }, [reportName, filters, reportsStore, setIsLoading]);

    useEffect(() => {
        if (!userPreferencesStore.reportsPreferences || !reportsStore.dateRange) return;

        const hasPresetFiltersForReport = Boolean(reportFilters[reportName]);
        const hasLocalFilters = Boolean(filters);

        // Case 1: no preset filters and no local filters.
        // Case 2: report has preset filters and local filters are present.
        if (hasPresetFiltersForReport === hasLocalFilters &&
            (!filtersReportName || filtersReportName === reportName)) {
            void loadReportData();
        }
    }, [reportName, filters, filtersReportName, reportsStore.dateRange, userPreferencesStore.reportsPreferences, loadReportData]);

    useEffect(() => {
        if (userPreferencesStore.data && !reportsStore.dateRange) {
            const {dateRange, timeFrame} = userPreferencesStore.data.userPreferences.pages.reportsPage;
            let value;
            if (dateRange) {
                const [fromStr, toStr] = dateRange.split('-');
                value = {from: new Date(fromStr), to: new Date(toStr)};
            } else {
                value = timeFrame;
            }
            reportsStore.setDateRange(value);
        }
    }, [userPreferencesStore.data]);

    return (
        <div className={`report-content ${isLoading ? 'loading' : ''}`}>
            {hasError && !isLoading && <TryAgainMessage onclick={loadReportData}/>}
            {isLoading && <Loader type={'component'}/>}
            {filteredData && <div style={isLoading ? {display: 'none'} : undefined}>
                <ReportRenderer reportName={reportName} data={filteredData}/>
            </div>}
        </div>
    );
});
